import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaPaperPlane, FaPaperclip } from 'react-icons/fa';
import { API_URL } from '../config';

const InputContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: 15px 20px;
  background-color: white;
  border-top: 1px solid #e0e0e0;
`;

const InputForm = styled.form`
  display: flex;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 45px 12px 15px;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  resize: none;
  min-height: 45px;
  max-height: 120px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  outline: none;
  
  &:focus {
    border-color: #0066cc;
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
  }
`;

const SendButton = styled.button`
  position: absolute;
  right: 10px;
  bottom: 10px;
  background: none;
  border: none;
  color: #0066cc;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const UploadButton = styled.button`
  position: absolute;
  right: 45px;
  bottom: 10px;
  background: none;
  border: none;
  color: #0066cc;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  
  &:hover {
    color: #004999;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  background-color: #f0f7ff;
  border-radius: 4px;
  padding: 5px 10px;
  margin-bottom: 10px;
  font-size: 12px;
  max-width: 800px;
  margin: 0 auto 10px auto;
`;

const FileName = styled.span`
  margin-right: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: #ff4d4f;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 5px;
  
  &:hover {
    color: #ff1a1a;
  }
`;

const ChatInput = ({ onSendMessage, disabled, activeChat }) => {
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);
  
  const handleTextareaChange = (e) => {
    setMessage(e.target.value);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('File size exceeds 5MB limit');
        return;
      }
      
      // Check file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.txt', '.md'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setUploadError('Invalid file type. Only PDF, DOC, DOCX, TXT, and MD files are allowed.');
        return;
      }
      
      setSelectedFile(file);
      setUploadError('');
    }
  };
  
  const handleFileUpload = async () => {
    if (!selectedFile || !activeChat) return;
    
    setIsUploading(true);
    setUploadError('');
    
    try {
      // Get auth token from localStorage instead of cookies
      const authToken = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('session_id', activeChat);
      
      const response = await fetch(`${API_URL}/chat-upload/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          // Don't set Content-Type with FormData, browser will set it with boundary
          // Add Authorization header with the token from localStorage
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload file');
      }
      
      console.log('File upload successful:', data);
      
      // Send a message with the file ID
      onSendMessage('', data.file.id);
      
      // Clear the file input
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFile) {
      await handleFileUpload();
      return;
    }
    
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
      
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };
  
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
  
  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setUploadError('');
  };
  
  return (
    <InputContainer>
      {selectedFile && (
        <FilePreview>
          <FileName>{selectedFile.name}</FileName>
          <RemoveFileButton onClick={removeSelectedFile}>Remove</RemoveFileButton>
        </FilePreview>
      )}
      {uploadError && (
        <FilePreview style={{ backgroundColor: '#fff1f0', color: '#ff4d4f' }}>
          {uploadError}
          <RemoveFileButton onClick={() => setUploadError('')}>Dismiss</RemoveFileButton>
        </FilePreview>
      )}
      <InputForm onSubmit={handleSubmit}>
        <TextArea
          ref={textareaRef}
          value={message}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder={selectedFile ? "Ask a question about this document..." : "Type a message..."}
          disabled={disabled || isUploading}
        />
        <FileInput 
          type="file" 
          ref={fileInputRef} 
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt,.md"
        />
        <UploadButton 
          type="button" 
          onClick={handleUploadClick} 
          disabled={disabled || isUploading}
          title="Upload document (max 5MB)"
        >
          <FaPaperclip />
        </UploadButton>
        <SendButton 
          type="submit" 
          disabled={(!message.trim() && !selectedFile) || disabled || isUploading}
          title={selectedFile ? "Upload and analyze document" : "Send message"}
        >
          <FaPaperPlane />
        </SendButton>
      </InputForm>
    </InputContainer>
  );
};

export default ChatInput;
